// Modules
import { RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { t } from 'i18next';

// Components
import { LoaderComponent } from '../../../../../loader/loader.component';
import { AudioAnalyser } from './components/audio/AudioAnalyser';
import { JoystickDishComponent } from './components/joystick/joystick.dish.component';
import { MutedDishComponent } from './components/muted/muted.dish.component';

// Helpers
import { UserSettingsHelper } from '../../../../../../helpers/UserSettingsHelper';

// Properties
import { DishVideoSettings } from './components/settings/dish.video.settings';

// Styles
import './dish.video.styles.scss';

// Properties
import { VideoProperties } from './video.properties';
import { ControlsButtonVideoType } from './dish.video.controls.types';
import moment from 'moment';
import DishVideoProfile from './components/profile/dish.video.profile';
import { UserType } from '../../../../../../types/user.type';
import DishMutedWidget from './components/mutedWidget/mutedWidget.component';
import { NickComponent } from '../../../../../nick/nick.component';
import { StepSliderComponent } from './components/settings/components/stepSlider/step-slider.component';
import { StreamInfoComponent } from './components/stream-info/stream-info.component';
import apiClient from '../../../../../../helpers/apiClient';
import { useElectron } from '../../../../../../hooks/useElectron';

/**
 * Video Component
 * @description Component to show a video
 * @param {VideoProperties} properties
 * @returns {JSX.Element}
 */
export function VideoComponent(properties: VideoProperties): JSX.Element {

    // ref of video
    const ref = useRef<HTMLVideoElement>(null);

    const { isElectron } = useElectron();

    // ref of surface
    const surfaceRef = useRef<HTMLDivElement>(null);

    // audio status
    const [isMuted, setIsMuted] = useState<boolean>(properties.muted || false);

    // show settings
    const [showSettings, setShowSettings] = useState<boolean>(false);

    // loading to avoid flickering
    const [loading, setLoading] = useState<boolean>(true);

    // flip video
    const [flip, setFlip] = useState<boolean>(false);

    // audio output device
    const [audioOutputDevice, setAudioOutputDevice] = useState<string>();

    // audio output device label
    const [audioOutputDeviceLabel, setAudioOutputDeviceLabel] = useState<string>();

    // audio input device
    const [audioInputDevice, setAudioInputDevice] = useState<string>();

    // audio input device label
    const [audioInputDeviceLabel, setAudioInputDeviceLabel] = useState<string>();

    // video input device
    const [videoInputDevice, setVideoInputDevice] = useState<string>();

    // video input device label
    const [videoInputDeviceLabel, setVideoInputDeviceLabel] = useState<string>();

    // status video
    const [statusVideo, setStatusVideo] = useState<boolean>(true);

    // status audio
    const [statusAudio, setStatusAudio] = useState<boolean>(true);

    // animation
    const [animation, setAnimation] = useState<boolean>(false);

    // PTZ detected
    const [ptzDetected, setPTZDetected] = useState<boolean>(false);

    // User
    const [user, setUser] = useState<UserType|undefined>(undefined);

    // Show config button
    const [showConfig, setShowConfig] = useState<boolean>(false);

    // Show muted widget
    const [showMutedWidget, setShowMutedWidget] = useState<boolean>(false);

    // show slider
    const [isSliderVisible, setIsSliderVisible] = useState<boolean>(false);

    const [manualHelp, setManualHelp] = useState<string>('');
    const [helpVisible, setHelpVisible] = useState<boolean>(false);
    const [helpName, setHelpName] = useState<string>('');
    const [helpContent, setHelpContent] = useState<string>('');

    // Audio recording states
    const [isRecording, setIsRecording] = useState<boolean>(false);
    const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
    const [audioChunks, setAudioChunks] = useState<BlobPart[]>([]);

    // Initialize audio output device from localStorage
    useEffect(() => {
        const savedAudioOutputDeviceId = localStorage.getItem('audioOutputDeviceId');
        if (savedAudioOutputDeviceId && properties.getAudioOutputDevices) {
            // Verify device still exists
            properties.getAudioOutputDevices().then(devices => {
                const deviceExists = devices?.find(device => device.deviceId === savedAudioOutputDeviceId);
                if (deviceExists) {
                    setAudioOutputDevice(savedAudioOutputDeviceId);
                    setAudioOutputDeviceLabel(deviceExists.label);

                    // Apply to current elements immediately
                    setTimeout(() => {
                        const videoElements = document.querySelectorAll('video, audio');
                        videoElements.forEach(element => {
                            try {
                                // @ts-ignore
                                if (element.setSinkId && !element.muted) {
                                    // @ts-ignore
                                    element.setSinkId(savedAudioOutputDeviceId).catch(console.error);
                                }
                            } catch (error) {
                                console.error('Error setting initial audio output device:', error);
                            }
                        });
                    }, 1000);
                }
            });
        }
    }, [properties.getAudioOutputDevices]);

    useEffect(() => {
      return () => {
        if (isRecording && mediaRecorder) {
          console.log('Stopping recording on unmount');
          mediaRecorder.stop();
          setIsRecording(false);
        }
      };
    }, [isRecording, mediaRecorder]);

    // Start recording audio
    const startSavingAudio = () => {
      if (isRecording || !properties.stream) return;

      try {
        // Create a new stream with only audio tracks
        const audioStream = new MediaStream(
          properties.stream.getAudioTracks()
        );

        if (audioStream.getAudioTracks().length === 0) {
          console.error('No audio tracks found in stream');
          return;
        }

        // Create media recorder
        const recorder = new MediaRecorder(audioStream);
        setMediaRecorder(recorder);

        // Clear previous chunks
        setAudioChunks([]);

        // Set up event handlers
        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            setAudioChunks(prev => [...prev, event.data]);
          }
        };

        // Start recording
        recorder.start(100); // Collect data every 100ms
        setIsRecording(true);
      } catch (error) {
        console.error('Error starting audio recording:', error);
      }
    };

    // Stop recording audio
    const stopSavingAudio = () => {
      if (!isRecording || !mediaRecorder) {
        console.error('No recording in progress');
        return;
      }

      // download audio
      saveRecordedAudio();

      mediaRecorder.stop();
      setMediaRecorder(null);

      setIsRecording(false);
    };

    // Save recorded audio with user-selected location
    const saveRecordedAudio = async () => {
      if (audioChunks.length === 0) {
        console.error('No audio chunks to save');
        return;
      }

      // Create blob from chunks
      const audioBlob = new Blob(audioChunks, { type: 'audio/mp3' });
      const filename = `audio_${moment().format('YYYYMMDDHHmmss')}.mp3`;

      try {
        // Check if running in Electron
        if (isElectron) {
          // Create a temporary URL to pass to the saveFileInElectron function
          const url = URL.createObjectURL(audioBlob);

          // Use the existing electron save method with our audio blob
          const element = document.createElement('a');
          element.href = url;
          element.setAttribute('download', filename);
          element.style.display = 'none';
          document.body.appendChild(element);
          element.click();
          document.body.removeChild(element);
          URL.revokeObjectURL(url);
        } else {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'Audio Files',
              accept: {
                'audio/mp3': ['.mp3'],
              },
            }],
          });
          const writable = await fileHandle.createWritable();
          await writable.write(audioBlob);
          await writable.close();
        }

        // Clear audio chunks after saving
        setAudioChunks([]);
      } catch (error) {
        console.error('Error saving audio file:', error);
      }
    };

    // Agrega este useEffect al componente VideoComponent
    useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.hidden && ref.current) {
          // Intentar continuar la reproducción cuando la página está oculta
          const playPromise = ref.current.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.log("Auto-play prevented in background:", error);
            });
          }
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      };
    }, []);

    useEffect(() => {
      if (manualHelp) {
        apiClient.get(`/api/manuals/${manualHelp}`)
      .then(response => {
        setHelpName(response.data.name);
        setHelpContent(response.data.content);
      })
      .catch(error => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
      }
    }, [manualHelp]);

    // show stream info
    const [showStreamInfo, setShowStreamInfo] = useState<boolean>(false);

    const showConfigTypes = useMemo(() => ['local'], []);
    const showMutedWidgetTypes = useMemo(() => ['local', 'remote'], []);

    const updateAudioStatus = useCallback(() => {
      if (properties.stream.getAudioTracks().length > 0) {
        setStatusAudio(properties.stream.getAudioTracks()[0].enabled);
        setAudioOutputDeviceLabel(properties.stream.getAudioTracks()[0].label);
        setAudioInputDeviceLabel(properties.stream.getAudioTracks()[0].label);
      } else {
        setStatusAudio(false);
        setAudioOutputDeviceLabel('');
        setAudioInputDeviceLabel('');
      }
    }, [properties.stream]);

    const updateVideoStatus = useCallback(() => {
      if (properties.stream.getVideoTracks().length > 0) {
        setStatusVideo(properties.stream.getVideoTracks()[0].enabled);
        setVideoInputDeviceLabel(properties.stream.getVideoTracks()[0].label);
      } else {
        setStatusVideo(false);
        setVideoInputDeviceLabel('');
      }
    }, [properties.stream]);

    useLayoutEffect(() => {
      setShowConfig(showConfigTypes.includes(properties.type));
      setShowMutedWidget(showMutedWidgetTypes.includes(properties.type));
    }, [properties.type, showConfigTypes, showMutedWidgetTypes]);

    useEffect(() => {
      if (ref.current && ref.current.srcObject !== properties.stream) {
        ref.current.srcObject = properties.stream;
      }

      if (properties.muted) setIsMuted(properties.muted);

      if (!properties.stream.active) {
        properties?.setDeviceDisconnected(true);
      }

      updateAudioStatus();
      updateVideoStatus();
    }, [properties.stream, properties.muted, properties.audioStatus, properties.videoStatus]);

    // Change volume of stream
    useEffect(() => {
      if (ref.current && ref.current instanceof HTMLAudioElement && properties.type === 'med.device') {
        if (properties.estetoVolume <= 100) {
          // Comportamiento normal hasta 100%
          ref.current.volume = properties.estetoVolume / 100;

          // Desactivar Web Audio API si estaba activa
          if (window.audioSource) {
            window.audioSource.disconnect();
            window.audioSource = undefined;
          }
          if (window.gainNode) {
            window.gainNode.disconnect();
            window.gainNode = undefined;
          }
        } else {
          // Para valores >100%, usamos Web Audio API para amplificar
          try {
            // Asegurar que tenemos un stream
            if (!ref.current.srcObject) return;

            // Configurar volumen base al máximo (100%)
            ref.current.volume = 0; // Silenciamos el elemento original

            // Crear un contexto de audio si no existe
            if (!window.audioContext) {
              window.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            }

            // Si ya hay una conexión establecida, desconectarla
            if (window.audioSource) {
              window.audioSource.disconnect();
            }
            if (window.gainNode) {
              window.gainNode.disconnect();
            }

            // Crear nuevas conexiones
            const stream = ref.current.srcObject as MediaStream;
            window.audioSource = window.audioContext.createMediaStreamSource(stream);
            window.gainNode = window.audioContext.createGain();

            // Calcular el valor de ganancia
            // 100 → 1.0, 200 → 2.0 (proporción directa)
            const gainValue = 1.0 + ((properties.estetoVolume - 100) / 100);
            window.gainNode.gain.value = gainValue;

            console.log('Gain value:', gainValue);

            // Conectar nodos
            window.audioSource.connect(window.gainNode);
            window.gainNode.connect(window.audioContext.destination);
          } catch (error) {
            console.warn('Error al aplicar amplificación de audio:', error);
            // Fallback: dejar al máximo normal
            ref.current.volume = 1.0;
          }
        }
      }
    }, [properties.estetoVolume]);

    useEffect(() => {
      return () => {
        // Limpiar recursos de Web Audio API al desmontar
        if (window.audioSource) {
          window.audioSource.disconnect();
          window.audioSource = undefined;
        }
        if (window.gainNode) {
          window.gainNode.disconnect();
          window.gainNode = undefined;
        }
        if (window.audioContext) {
          // No cerramos el contexto para evitar problemas con otras instancias
          // pero desconectamos nuestros nodos
        }
      };
    }, []);

    useEffect(() => {
      const showConfigTypes = ['local'];
      const showMutedWidgetTypes = ['local', 'remote'];

      if (showConfigTypes.includes(properties.type)) {
        setShowConfig(true);
      }

      if (showMutedWidgetTypes.includes(properties.type)) {
        setShowMutedWidget(true);
      }
    }, []);

    // useEffect(() => {
    //     // check if stream have audio enabled
    //     if (ref.current && audioOutputDevice) {

    //         // change audio output device
    //         // @ts-ignore
    //         ref.current.setSinkId(audioOutputDevice);

    //     }

    // }, [audioOutputDevice]);

    useEffect(() => {
        // Apply audio output device change
        if (ref.current && audioOutputDevice) {
            // @ts-ignore
            if (ref.current.setSinkId && !isMuted && !ref.current.muted) {
                // @ts-ignore
                ref.current.setSinkId(audioOutputDevice).then(() => {
                    console.log('Audio output successfully changed to device:', audioOutputDevice);
                }).catch((error: Error) => {
                    console.error('Error changing audio output device:', error);
                });
            } else {
                console.warn('setSinkId not supported, element is muted, or element not ready');
            }
        }
    }, [audioOutputDevice, isMuted]);

    const toggleVideoStatus = useCallback(() => {
      // kill video of stream
      const videoStream = properties.stream.getVideoTracks()[0];

      // replace video from black
      if (videoStream && properties.type === 'local') {

          setStatusVideo(!videoStream.enabled);
          properties.stream.getVideoTracks()[0].enabled = !videoStream.enabled;

          properties.socketInstance.emit('stream:status_change',
              {
                status: videoStream.enabled,
                streamId: properties.stream.id,
                room: properties.room,
                type: 'video'
              });

      }
    }, [properties.stream, properties.socketInstance, properties.room, properties.type]);

    const toggleAudioStatus = useCallback(() => {
        // kill audio of stream
        const audioStream = properties.stream.getAudioTracks()[0];

        // replace audio from black
        if (audioStream && properties.type === 'local') {

            setStatusAudio(!audioStream.enabled);
            properties.stream.getAudioTracks()[0].enabled = !audioStream.enabled;

            properties.socketInstance.emit('stream:status_change',
                {
                  status: audioStream.enabled,
                  streamId: properties.stream.id,
                  room: properties.room,
                  type: 'audio'
                });

        }
    }, [properties.stream, properties.socketInstance, properties.room, properties.type]);

    useEffect(() => {

    }, [properties.stream]);

    useEffect(() => {
        if (properties.videoStatus !== undefined && properties.videoStatus !== statusVideo) {
            toggleVideoStatus();
        }
    }, [properties.videoStatus, statusVideo, toggleVideoStatus]);

    useEffect(() => {
        if (properties.audioStatus !== undefined && properties.audioStatus !== statusAudio) {
            toggleAudioStatus();
        }
    }, [properties.audioStatus, statusAudio, toggleAudioStatus]);

    const isDataUriBase64Image = (str: string) => {
        return /^data:image\/(png|jpg|jpeg|gif|svg\+xml);base64,/.test(str);
    }

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            setUser(JSON.parse(storedUser));
        }
    }, []);

    const getButtonSize = (): string => {
      switch (properties.totalParticipants) {
        case 0:
        case 1:
        case 2:
          return 'btn-lg';
        case 3:
        case 4:
          return 'btn-md';
        case 5:
          return 'btn-sm';
        default:
          return 'btn-sm';
      }
    }

    /**
     * Render a generic HTML button with icon
     *
     * @param {ControlsButtonVideoType} settings
     * @returns {JSX.Element|undefined}
     */
    const renderButton = (settings: ControlsButtonVideoType): JSX.Element|undefined => {

        const classNames = [
            'button',
            getButtonSize(),
            settings.className || '',
        ].join(' ');

        const button = UserSettingsHelper.getUserSettingsButton(properties.userSettings, settings.name);

        let icon: string = (settings.active ? button?.icons?.active : button?.icons?.inactive) || settings.icon;

        const isDataUri = isDataUriBase64Image(icon);

        if (button) {
            // Botón no visible en los ajustes
            if (!button.visible) return;

            return <button title={settings.title} className={classNames} onClick={settings.onClick} >
                {
                    isDataUri ? <img style={{ width: '38px' }} src={icon} alt={settings.name} /> : <i className={icon} />
                }
            </button>
        }

        return <button title={settings.title} className={classNames} onClick={settings.onClick} >
            {
                isDataUri ? <img style={{ width: '38px' }} src={icon} alt={settings.name} /> : <i className={icon} />
            }
        </button>
    }

    const isOnlyAudio = (properties.stream.getVideoTracks().length === 0 && properties.stream.getAudioTracks().length > 0);
    const isOnlyVideo = (properties.stream.getAudioTracks().length === 0 && properties.stream.getVideoTracks().length > 0);

    // get label
    const label = properties.stream.getVideoTracks().length > 0 ? properties.stream.getVideoTracks()[0].label : '';

    // kind of stream streamSettings?.kind
    const audioStreamSettings: MediaStreamTrack | undefined = properties.stream.getAudioTracks().length > 0 ? properties.stream.getAudioTracks()[0] : undefined;
    const videoStreamSettings: MediaStreamTrack | undefined = properties.stream.getVideoTracks().length > 0 ? properties.stream.getVideoTracks()[0] : undefined;

    // class name of video
    const classNames = [
        isOnlyAudio ? 'audio' : '',
        isMuted ? 'muted' : '',
        properties.layout === 'focus'
          ? 'contain'
          : properties.type === 'share.screen' || properties.type === 'med.device' ? 'contain' : 'fill',
    ].join(' ');

    useEffect(() => {
        setTimeout(() => {
            // set animation
            setAnimation(true)
        }, 4000);
    }, [])

    useEffect(() => {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }, [isOnlyAudio, isOnlyVideo, properties.audioStatus, properties.videoStatus])

    const saveFileInElectron = (canvas: HTMLCanvasElement, filename: string = `captura_${moment().format('YYYYMMDDHHmmss')}.png`) => {
      const element = document.createElement('a');
      const dataURL = canvas.toDataURL('image/png');

      element.setAttribute('href', dataURL);
      element.setAttribute('download', filename);
      element.style.display = 'none';
      document.body.appendChild(element);

      element.click();

      document.body.removeChild(element);
    }

    // Handle keydown event for show stream info
    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.ctrlKey && event.altKey && event.key === 'i') {
          setShowStreamInfo(prevShowStreamInfo => !prevShowStreamInfo);
        }
      };

      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, []);

    const saveFileInBrowser = async (canvas: HTMLCanvasElement, options: SaveFilePickerOptions) => {
      const directoryHandle = await window.showSaveFilePicker(options);
      const writable = await directoryHandle.createWritable();
      // const writable = await fileHandle.createWritable();

      // Get thumbnail data
      canvas.toBlob(async (blob) => {
        if (blob) {
          await writable.write(blob);
          await writable.close();
        }
      }, "image/png");
    }

    const handleScreenshot = async (
      ref: RefObject<HTMLVideoElement>,
      aspectRatio: string,
      options: SaveFilePickerOptions,
    ): Promise<FileSystemHandle | undefined> => {
      // Check if the Native File System API is supported.
      if (window.showDirectoryPicker === undefined) {
        return undefined;
      }

      const arrayAspect = aspectRatio.split(':');

      try {
        // Create canvas:
        let canvas = document.createElement("canvas");

        // Calculate canvas size
        let canvasWidth = parseInt(arrayAspect[0]);
        let canvasHeight = parseInt(arrayAspect[1]);

        // Set canvas size
        canvas.setAttribute("width", `${canvasWidth}`);
        canvas.setAttribute("height", `${canvasHeight}`);

        // Generate thumbnail URL data
        let context = canvas.getContext("2d");
        if (context) {
          if (flip) {
            // flip video frame
            context.translate(ref.current?.videoWidth || 0, 0);
            context.scale(-1, 1);
          }

          // Draw video frame to canvas
          context.drawImage(ref.current!, 0, 0, canvas.width, canvas.height);

          if (isElectron) {
            saveFileInElectron(canvas, options.suggestedName);
            return;
          }

          await saveFileInBrowser(canvas, options);

          // Write thumbnail data to file
          //await writable.write(data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    return <div className={`DishVideoComponent ${animation ? 'animated' : ''} ${properties.videoBigSelected ? 'selected' : 'unselected'}`}>
        {showSettings && <DishVideoSettings
            getAudioOutputDevices={properties.getAudioOutputDevices}
            getVideoInputDevices={properties.getVideoInputDevices}
            getAudioInputDevices={properties.getAudioInputDevices}
            setAudioOutputDevice={(deviceId: string, label: string) => {

                setAudioOutputDevice(deviceId);
                setAudioOutputDeviceLabel(label);
            }
            }
            setVideoInputDevice={(deviceId: string, label: string) => {

              setVideoInputDevice(deviceId);
              setVideoInputDeviceLabel(label);

            }
            }
            setAudioInputDevice={(deviceId: string, label: string) => {

              setAudioInputDevice(deviceId);
              setAudioInputDeviceLabel(label);

            }
            }
            audioOutputDevice={audioOutputDevice}
            audioOutputDeviceLabel={audioOutputDeviceLabel}
            audioInputDevice={audioInputDevice}
            audioInputDeviceLabel={audioInputDeviceLabel}
            videoInputDevice={videoInputDevice}
            videoInputDeviceLabel={videoInputDeviceLabel}
            flip={flip}
            flipVideo={() => videoStreamSettings && setFlip(!flip)}
            stream={properties.stream} setShowSettings={() => setShowSettings(!showSettings)}
            socketInstance={properties.socketInstance}
            deviceInstance={properties.deviceInstance}
            />}
        {showStreamInfo && <StreamInfoComponent
            stream={properties.stream}
            setShowStreamInfo={() => setShowStreamInfo(!showStreamInfo)}
        />}


        <div className='joystick-container'>

        </div>
        {
            loading && <LoaderComponent status={true} />
        }
        <div ref={surfaceRef} className={`render ${loading ? 'loading' : ''} ${!flip ? 'flip' : ''}`}>
            {
                /* NOTE: If the stream is only audio, we will show the audio analyser */
                (isOnlyAudio && !isOnlyVideo) || (properties.audioStatus && !properties.videoStatus)
                && properties.stream.getAudioTracks().length > 0
                    ? <>
                        <AudioAnalyser audio={properties.stream} />
                        {properties.stream && <audio ref={ref} autoPlay muted={isMuted} />}
                    </>
                    : !properties.audioStatus && !properties.videoStatus
                        ? <DishVideoProfile
                            name={properties.name}
                            hasToShowIcon={properties.type === 'local' ? true : false}
                            showSettings={showSettings}
                            setShowSettings={setShowSettings}
                            />
                        : <video
                            playsInline
                            onLoadedData={() => setTimeout(() => setLoading(false), 1000)}
                            controls={false}
                            className={classNames}
                            ref={ref}
                            autoPlay
                            muted={isMuted}
                        />
            }
        </div>

        {/* Muted Widget */}
        {
          showMutedWidget && !properties.audioStatus && <DishMutedWidget layout={properties.layout} />
        }

        {
          (properties.audioStatus || properties.videoStatus) && <NickComponent user={properties.user} />
        }

        <div className={`controls ${loading ? 'loading' : ''}`}>
            {
              isOnlyAudio && !isOnlyVideo &&
              <button
                    onClick={() => isRecording ? stopSavingAudio() : startSavingAudio()}
                    title={isRecording ? t('Stop recording audio').toString() : t('Start recording audio').toString()}
                    className='recording-button'
                    >
                    <i className={`las la-circle ${isRecording ? 'blinking-effect' : ''}`} />
                  </button>
            }
            {
                isOnlyAudio && !isOnlyVideo &&
                properties.type === 'med.device' &&
                <>
                  <MutedDishComponent
                    isRemote={properties.isRemoteStream}
                    isMuted={isMuted}
                    userSettings={properties.userSettings}
                    setIsMuted={setIsMuted}
                    streamType={properties.type}
                  />
                  <button onClick={() => setIsSliderVisible(!isSliderVisible)}>
                    <i className="las la-cog" />
                  </button>
                </>
            }

            {
              properties.manual_id &&
              <>
              <button onClick={() => {
                setManualHelp(properties.manual_id);
                setHelpVisible(true);
                properties.setIsShowingHelp(true);
              }}>
                <i className="las la-question-circle" />
              </button>
              </>
            }

            {
              helpVisible && (
                <div className='HelpView'>
                  <div className='HelpContainer'>
                    <div className='Header'>
                      <div className='Title'>
                        {helpName}
                      </div>
                      <i className="las la-times last" onClick={() => {
                        setHelpVisible(false);
                        properties.setIsShowingHelp(false);
                      }} />
                    </div>
                    <div className='Body'>
                      <div dangerouslySetInnerHTML={{ __html: helpContent }} />
                    </div>
                  </div>
                </div>
              )
            }

            {
              isSliderVisible && <div className='Modal Right'>
                <div className='Header'>
                  Volumen del estetoscopio
                  <i className="las la-times last" onClick={() => {
                    setIsSliderVisible(false);
                  }} />
                </div>
                <StepSliderComponent
                  title={t('volume.title').toString()}
                  steps={200}
                  min={0}
                  max={200}
                  selectedValue={properties.estetoVolume}
                  setSelectedValue={properties.setEstetoVolume}
                />
              </div>
            }

            {/* Screenshots Button */}
            {/* { (isOnlyVideo || properties.videoStatus) && videoStreamSettings && renderButton({
                name: 'video.screenshot',
                title: t('button.video.screenshot.title').toString(),
                icon: 'las la-camera',
                active: true,
                onClick: () => {
                    handleScreenshot(ref, {
                        suggestedName: `captura_${moment().format('YYYYMMDDHHmmss')}.png`,
                        types: [{
                            description: 'PNG',
                            accept: {
                                'image/png': ['.png'],
                            },
                        }],
                    });
                },
            })
            } */}

            {/* Enable/Disable Video Button - DISABLED */}
            {
                false && videoStreamSettings && properties.type === 'local' && renderButton({
                    name: 'video.toggle',
                    title: t('button.video.toggle.title').toString(),
                    active: statusVideo,
                    icon: statusVideo ? 'las la-video' : 'las la-video-slash',
                    onClick: () => {
                        toggleVideoStatus();
                    }
                })
            }

            {/* Enable/Disable Audio Button - DISABLED*/}
            {
                false && audioStreamSettings && properties.type === 'local' && renderButton({
                    name: 'video.audio.toggle',
                    title: t('button.video.audio.toggle.title').toString(),
                    active: statusAudio,
                    icon: statusAudio ? 'las la-microphone' : 'las la-microphone-slash',
                    onClick: () => {
                        toggleAudioStatus();
                    }
                })
            }

            {/* PTZ Control Button */}
            {
              properties.type !== 'med.device'
                && <JoystickDishComponent
                      renderButton={renderButton}
                      stream={properties.stream}
                      onPTZ={setPTZDetected}
                      streamTrack={properties.streamTrack}
                      socketInstance={properties.socketInstance}
                      room={properties.room}
                      isRemoteStream={properties.isRemoteStream}
                      streamId={properties.stream.id}
                      onPtzCommandReceived={properties.onPtzCommandReceived}
                      savePtzPreset={properties.savePtzPreset}
                    />}

            {/* PTZ Remote Control Button */}
            {
              properties.isRemoteStream &&
              properties.clientPtzPermission &&
              properties.ptzDeviceInfo &&
              <JoystickDishComponent
                renderButton={renderButton}
                stream={properties.stream}
                onPTZ={setPTZDetected}
                streamTrack={properties.streamTrack}
                socketInstance={properties.socketInstance}
                room={properties.room}
                isRemoteStream={properties.isRemoteStream}
                streamId={properties.stream.id}
                ptzDeviceInfo={properties.ptzDeviceInfo}
                onPtzCommandReceived={properties.onPtzCommandReceived}
              />
            }

            {/* Ptz Flip Button */}
            {/* {
                statusVideo && videoStreamSettings && ptzDetected && renderButton({
                    name: 'video.ptz.flip',
                    title: t('button.video.ptz.flip.title').toString(),
                    icon: 'data:image/svg+xml;base64,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',
                    active: flip,
                    onClick: () => {
                        setFlip(!flip);
                    }
                })
            } */}

            {/* Status Button */}
            {/* {
                (properties.audioStatus || properties.videoStatus) &&
                showConfig && renderButton({
                    name: 'video.status',
                    title: t('button.video.status.title').toString(),
                    active: showSettings,
                    icon: 'las la-cog',
                    onClick: () => {
                        setShowSettings(!showSettings);
                    }
                })
            } */}
        </div>

        <div style={
          {
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '10px'
          }
        }>
          {/* Status Button */}
          {
            (properties.audioStatus || properties.videoStatus) &&
            showConfig &&
            <>
              <i className="las la-cog" onClick={() => {
                setShowSettings(!showSettings);
              } } style={
                {
                  color: 'rgba(23, 44, 84, 0.891)',
                  cursor: 'pointer',
                  fontSize: '1.5rem',
                  backgroundColor: 'white',
                  padding: '10px',
                  borderRadius: '50%',
                  zIndex: 999
                }
              }></i>
            </>
          }
          {/* Screenshots Button */}
          {
            (isOnlyVideo || properties.videoStatus)
            && videoStreamSettings
            && (properties.clientScreenshotPermission || properties.type === 'med.device')
            &&
            <i className="las la-camera" onClick={() => {
              let aspectRatio = '16:9';

              // get aspect ratio
              if (ref.current) {
                aspectRatio = `${ref.current.videoWidth}:${ref.current.videoHeight}`;

                console.log('Aspect ratio:', aspectRatio);
              }

              handleScreenshot(ref,
                aspectRatio,
                {
                suggestedName: `captura_${moment().format('YYYYMMDDHHmmss')}.png`,
                types: [{
                  description: 'PNG',
                  accept: {
                    'image/png': ['.png'],
                  },
                }],
              });
            }
            } style={
              {
                color: 'rgba(23, 44, 84, 0.891)',
                cursor: 'pointer',
                fontSize: '1.5rem',
                backgroundColor: 'white',
                padding: '10px',
                borderRadius: '50%',
                zIndex: 999
              }
            }></i>
          }
        </div>

        {/* Selection Button */}
        {
            renderButton({
                name: 'video.selection',
                className: 'selection',
                active: true, // properties.videoBigSelected,
                icon: 'las la-thumbtack',
                onClick: () => properties.setVideoBigSelected(label),
            })
        }

        {/* Close Button */}
        {
            false && properties.type === 'local' && renderButton({
                name: 'video.close',
                className: 'remove',
                icon: 'las la-times',
                onClick: properties.removeStream
            })
        }
    </div>


};
